// The catalog of product events PostHog records — one entry per API write the
// app makes. `customFetch` hands every successful request to `trackApiRequest`
// (src/posthog.js), which captures the first entry matching it; a request no
// entry matches is not tracked. Add an entry here when you add a write
// endpoint, rather than calling PostHog from the screen that makes the request.
//
// Entry shape:
//   event       snake_case event name, `<object>_<past-tense verb>`
//   method      the effective method — a FormData PUT sent as POST + `_method=PUT`
//               still matches "PUT". Omit to match any method.
//   path        the API path without the language prefix or query string;
//               `:name` matches one dynamic segment
//   when        optional `(context) => boolean`, for one endpoint serving
//               several actions told apart by a body field. Entries are tried in
//               order, so a narrowed entry must precede a broader one on its path.
//   props       optional body fields copied onto the event as properties
//   properties  optional `(context) => object` for anything `props` cannot express
//   log         optional message also sent to the PostHog logs
//
// `when` and `properties` receive `{ url, method, body, response, meta, field }`:
// `field(key)` reads a body field whether the body is FormData or an object,
// `response` is the API envelope and `meta` is the `analytics` option a caller
// passed to `customFetch` for a fact the request does not carry.
//
// Never copy a field holding personal data (a name, a phone, an ID number) into
// `props` — only the enum-like fields that say which action this was.
//
// This file must import nothing: `services/api` imports posthog.js, which
// imports this.

export const API_EVENTS = [
  // ─── Accounting Periods ──────────────────────────────────────────────────────
  {
    event: "accounting_period_created",
    method: "POST",
    path: "dashboard/accounting-periods",
  },
  {
    event: "accounting_period_updated",
    method: "PUT",
    path: "dashboard/accounting-periods/:id",
  },
  {
    event: "accounting_period_closed",
    method: "POST",
    path: "dashboard/accounting-periods/:id/close",
  },
  {
    event: "accounting_period_reopened",
    method: "POST",
    path: "dashboard/accounting-periods/:id/reopen",
  },
  {
    event: "fiscal_year_closing_reopened",
    method: "POST",
    path: "dashboard/fiscal-year-closings/:id/reopen",
  },
  {
    event: "fiscal_year_closed",
    method: "POST",
    path: "dashboard/fiscal-year-closings/close",
  },
  // ─── Advertisements ──────────────────────────────────────────────────────────
  {
    event: "advertisement_status_changed",
    method: "PUT",
    path: "dashboard/advertisement-module/:id/status",
    props: ["status"],
  },
  // ─── Amer Support ────────────────────────────────────────────────────────────
  {
    event: "amer_support_ticket_closed",
    method: "PUT",
    path: "account/core-support/close/:id",
  },
  {
    event: "amer_support_ticket_created",
    method: "POST",
    path: "account/core-support/create",
  },
  {
    event: "amer_support_ticket_replied",
    method: "POST",
    path: "account/core-support/send-reply/:id",
  },
  // ─── Announcements ───────────────────────────────────────────────────────────
  {
    event: "announcements_marked_all_read",
    method: "POST",
    path: "announcements/mark-all-read",
  },
  // ─── Attendance ──────────────────────────────────────────────────────────────
  {
    event: "attendance_record_deleted",
    method: "DELETE",
    path: "dashboard/attendance/:id/delete",
  },
  {
    event: "attendance_record_updated",
    method: "PUT",
    path: "dashboard/attendance/:id/update",
  },
  {
    event: "attendance_record_created",
    method: "POST",
    path: "dashboard/attendance/create",
  },
  {
    event: "attendance_range_filled",
    method: "POST",
    path: "dashboard/attendance/fill",
  },
  {
    event: "work_shift_deleted",
    method: "DELETE",
    path: "dashboard/work-shifts/:id/delete",
  },
  {
    event: "work_shift_restored",
    method: "POST",
    path: "dashboard/work-shifts/:id/restore",
  },
  {
    event: "work_shift_updated",
    method: "PUT",
    path: "dashboard/work-shifts/:id/update",
  },
  {
    event: "work_shift_created",
    method: "POST",
    path: "dashboard/work-shifts/create",
  },
  // ─── Auth ────────────────────────────────────────────────────────────────────
  {
    event: "forgotten_password_reset",
    method: "POST",
    path: "admins/change-forget-password",
  },
  {
    event: "password_reset_otp_verified",
    method: "POST",
    path: "admins/check-otp",
  },
  {
    event: "password_reset_otp_requested",
    method: "POST",
    path: "admins/forget-password",
  },
  {
    event: "admin_deleted",
    method: "DELETE",
    path: "dashboard/admins/:id/delete",
  },
  {
    event: "admin_restored",
    method: "POST",
    path: "dashboard/admins/:id/restore",
  },
  {
    event: "admin_updated",
    method: "PUT",
    path: "dashboard/admins/:id/update",
  },
  {
    event: "password_changed",
    method: "POST",
    path: "dashboard/admins/change-password",
  },
  { event: "admin_created", method: "POST", path: "dashboard/admins/create" },
  {
    event: "admin_logged_out",
    method: "POST",
    path: "dashboard/admins/logout",
  },
  { event: "admin_logged_in", method: "POST", path: "dashboard/login" },
  {
    event: "passkey_registered",
    method: "POST",
    path: "dashboard/profile/security/passkeys",
  },
  {
    event: "passkey_device_removed",
    method: "DELETE",
    path: "dashboard/profile/security/passkeys/:id",
  },
  {
    event: "login_session_revoked",
    method: "DELETE",
    path: "dashboard/profile/security/sessions/:id",
  },
  {
    event: "other_sessions_revoked",
    method: "POST",
    path: "dashboard/profile/security/sessions/revoke-others",
  },
  {
    event: "two_factor_enrolment_confirmed",
    method: "POST",
    path: "dashboard/profile/security/two-factor/confirm",
  },
  {
    event: "two_factor_method_disabled",
    method: "POST",
    path: "dashboard/profile/security/two-factor/disable",
  },
  {
    event: "primary_two_factor_method_set",
    method: "POST",
    path: "dashboard/profile/security/two-factor/primary",
  },
  {
    event: "two_factor_enrolment_started",
    method: "POST",
    path: "dashboard/profile/security/two-factor/start",
  },
  {
    event: "login_passkey_verified",
    method: "POST",
    path: "dashboard/two-factor/passkey/verify",
  },
  {
    event: "two_factor_code_resent",
    method: "POST",
    path: "dashboard/two-factor/resend",
  },
  {
    event: "login_two_factor_verified",
    method: "POST",
    path: "dashboard/two-factor/verify",
  },
  { event: "profile_updated", method: "PUT", path: "dashboard/update-profile" },
  // ─── Bank Reconciliations ────────────────────────────────────────────────────
  {
    event: "bank_reconciliation_created",
    method: "POST",
    path: "dashboard/bank-reconciliations",
  },
  {
    event: "bank_reconciliation_deleted",
    method: "DELETE",
    path: "dashboard/bank-reconciliations/:id",
  },
  {
    event: "bank_reconciliation_updated",
    method: "PUT",
    path: "dashboard/bank-reconciliations/:id",
  },
  {
    event: "bank_reconciliation_completed",
    method: "POST",
    path: "dashboard/bank-reconciliations/:id/complete",
  },
  {
    event: "bank_statement_file_imported",
    method: "POST",
    path: "dashboard/bank-reconciliations/:id/import",
  },
  {
    event: "statement_line_flagged",
    method: "POST",
    path: "dashboard/bank-reconciliations/:id/lines/:lineId/flag",
  },
  {
    event: "statement_line_matched",
    method: "POST",
    path: "dashboard/bank-reconciliations/:id/lines/:lineId/match",
  },
  {
    event: "statement_line_unmatched",
    method: "POST",
    path: "dashboard/bank-reconciliations/:id/lines/:lineId/unmatch",
  },
  // ─── Banks ───────────────────────────────────────────────────────────────────
  {
    event: "bank_account_deleted",
    method: "DELETE",
    path: "dashboard/banks/:id/delete",
  },
  {
    event: "bank_account_restored",
    method: "POST",
    path: "dashboard/banks/:id/restore",
  },
  {
    event: "bank_account_updated",
    method: "PUT",
    path: "dashboard/banks/:id/update",
  },
  {
    event: "bank_account_created",
    method: "POST",
    path: "dashboard/banks/create",
  },
  // ─── Booking Contracts ───────────────────────────────────────────────────────
  {
    event: "accommodation_contract_created",
    method: "POST",
    path: "dashboard/booking-contracts",
  },
  {
    event: "accommodation_contract_deleted",
    method: "DELETE",
    path: "dashboard/booking-contracts/:id",
  },
  {
    event: "accommodation_contract_updated",
    method: "PUT",
    path: "dashboard/booking-contracts/:id",
  },
  {
    event: "accommodation_contract_cancelled",
    method: "PATCH",
    path: "dashboard/booking-contracts/:id/cancel",
    props: ["cancel_mode", "cancellation_reason"],
  },
  {
    event: "guest_checked_in",
    method: "POST",
    path: "dashboard/booking-contracts/:id/check-in",
  },
  {
    event: "guest_checked_out",
    method: "POST",
    path: "dashboard/booking-contracts/:id/check-out",
  },
  {
    event: "accommodation_contract_confirmed",
    method: "PATCH",
    path: "dashboard/booking-contracts/:id/confirm",
  },
  {
    event: "accommodation_contract_published",
    method: "POST",
    path: "dashboard/booking-contracts/:id/publish",
  },
  {
    event: "booking_confirmation_sent",
    method: "POST",
    path: "dashboard/booking-contracts/:id/send-confirmation",
  },
  // ─── Chat ────────────────────────────────────────────────────────────────────
  { event: "ai_chat_prompt_sent", method: "POST", path: "dashboard/ai/prompt" },
  {
    event: "ai_chat_thread_deleted",
    method: "DELETE",
    path: "dashboard/ai/threads/:id",
  },
  // ─── Contracts ───────────────────────────────────────────────────────────────
  {
    event: "rent_contract_deleted",
    method: "DELETE",
    path: "dashboard/contracts/:id/delete",
  },
  {
    event: "rent_contract_published",
    method: "POST",
    path: "dashboard/contracts/:id/publish",
  },
  {
    event: "rent_contract_restored",
    method: "POST",
    path: "dashboard/contracts/:id/restore",
  },
  {
    event: "rent_contract_terminated",
    method: "POST",
    path: "dashboard/contracts/:id/terminate",
    props: ["cancel_mode"],
  },
  {
    event: "rent_contract_ejar_unlinked",
    method: "DELETE",
    path: "dashboard/contracts/:id/unlink-ejar-auth",
  },
  {
    event: "rent_contract_updated",
    method: "PUT",
    path: "dashboard/contracts/:id/update",
  },
  {
    event: "contract_conditions_template_created",
    method: "POST",
    path: "dashboard/contracts/conditions-templates/create",
  },
  {
    event: "contract_conditions_template_deleted",
    method: "DELETE",
    path: "dashboard/contracts/conditions-templates/delete/:id",
  },
  {
    event: "contract_conditions_template_restored",
    method: "POST",
    path: "dashboard/contracts/conditions-templates/restore/:id",
  },
  {
    event: "contract_conditions_template_updated",
    method: "PUT",
    path: "dashboard/contracts/conditions-templates/update/:id",
  },
  {
    event: "rent_contract_created",
    method: "POST",
    path: "dashboard/contracts/create",
  },
  {
    event: "contract_attachment_renamed",
    method: "POST",
    path: "dashboard/contracts/file/:id",
  },
  {
    event: "contract_attachment_uploaded",
    method: "POST",
    path: "dashboard/contracts/upload-file",
  },
  {
    event: "sell_contract_deleted",
    method: "DELETE",
    path: "dashboard/sell-contracts/:id/delete",
  },
  {
    event: "sell_contract_published",
    method: "POST",
    path: "dashboard/sell-contracts/:id/publish",
  },
  {
    event: "sell_contract_restored",
    method: "POST",
    path: "dashboard/sell-contracts/:id/restore",
  },
  {
    event: "sell_contract_terminated",
    method: "POST",
    path: "dashboard/sell-contracts/:id/terminate",
  },
  {
    event: "sell_contract_updated",
    method: "PUT",
    path: "dashboard/sell-contracts/:id/update",
  },
  {
    event: "sell_contract_created",
    method: "POST",
    path: "dashboard/sell-contracts/create",
  },
  // ─── Crm ─────────────────────────────────────────────────────────────────────
  {
    event: "calendar_provider_disconnected",
    method: "DELETE",
    path: "dashboard/calendar-connections/:provider",
  },
  {
    event: "calendar_provider_connected",
    method: "POST",
    path: "dashboard/calendar-connections/:provider/connect",
  },
  {
    event: "crm_company_created",
    method: "POST",
    path: "dashboard/crm-companies",
  },
  {
    event: "crm_company_deleted",
    method: "DELETE",
    path: "dashboard/crm-companies/:id",
  },
  {
    event: "crm_company_updated",
    method: "PUT",
    path: "dashboard/crm-companies/:id",
  },
  {
    event: "crm_company_restored",
    method: "POST",
    path: "dashboard/crm-companies/:id/restore",
  },
  { event: "sales_team_created", method: "POST", path: "dashboard/crm-teams" },
  {
    event: "sales_team_deleted",
    method: "DELETE",
    path: "dashboard/crm-teams/:id",
  },
  {
    event: "sales_team_updated",
    method: "PUT",
    path: "dashboard/crm-teams/:id",
  },
  {
    event: "sales_team_restored",
    method: "POST",
    path: "dashboard/crm-teams/:id/restore",
  },
  { event: "deal_created", method: "POST", path: "dashboard/deals" },
  { event: "deal_deleted", method: "DELETE", path: "dashboard/deals/:id" },
  { event: "deal_updated", method: "PUT", path: "dashboard/deals/:id" },
  {
    event: "deal_assigned",
    method: "PUT",
    path: "dashboard/deals/:id/assign-agent",
  },
  {
    event: "deal_email_sent",
    method: "POST",
    path: "dashboard/deals/:id/email",
  },
  {
    event: "deal_favorite_toggled",
    method: "POST",
    path: "dashboard/deals/:id/favorite",
  },
  {
    event: "deal_interaction_logged",
    method: "POST",
    path: "dashboard/deals/:id/interactions",
  },
  {
    event: "deal_line_items_saved",
    method: "PUT",
    path: "dashboard/deals/:id/items",
  },
  {
    event: "deal_restored",
    method: "POST",
    path: "dashboard/deals/:id/restore",
  },
  {
    event: "deal_stage_changed",
    method: "PUT",
    path: "dashboard/deals/:id/stage",
  },
  {
    event: "crm_contact_created",
    method: "POST",
    path: "dashboard/lead_contacts",
  },
  {
    event: "crm_contact_deleted",
    method: "DELETE",
    path: "dashboard/lead_contacts/:id",
  },
  {
    event: "crm_contact_updated",
    method: "PUT",
    path: "dashboard/lead_contacts/:id",
  },
  {
    event: "crm_contact_restored",
    method: "POST",
    path: "dashboard/lead_contacts/:id/restore",
  },
  { event: "lead_created", method: "POST", path: "dashboard/leads" },
  {
    event: "lead_source_created",
    method: "POST",
    path: "dashboard/leads-sources",
  },
  {
    event: "lead_source_deleted",
    method: "DELETE",
    path: "dashboard/leads-sources/:id",
  },
  {
    event: "lead_source_updated",
    method: "PUT",
    path: "dashboard/leads-sources/:id",
  },
  {
    event: "lead_source_restored",
    method: "POST",
    path: "dashboard/leads-sources/:id/restore",
  },
  { event: "lead_deleted", method: "DELETE", path: "dashboard/leads/:id" },
  { event: "lead_updated", method: "PUT", path: "dashboard/leads/:id" },
  {
    event: "lead_assigned",
    method: "PUT",
    path: "dashboard/leads/:id/assign-agent",
  },
  {
    event: "lead_converted",
    method: "POST",
    path: "dashboard/leads/:id/convert",
  },
  {
    event: "lead_email_sent",
    method: "POST",
    path: "dashboard/leads/:id/email",
  },
  {
    event: "lead_favorite_toggled",
    method: "POST",
    path: "dashboard/leads/:id/favorite",
  },
  {
    event: "lead_interaction_logged",
    method: "POST",
    path: "dashboard/leads/:id/interactions",
  },
  {
    event: "lead_interaction_note_updated",
    method: "PUT",
    path: "dashboard/leads/:id/interactions/:interactionId",
  },
  {
    event: "lead_contact_attempt_logged",
    method: "POST",
    path: "dashboard/leads/:id/log-contact",
  },
  { event: "lead_merged", method: "POST", path: "dashboard/leads/:id/merge" },
  {
    event: "lead_restored",
    method: "POST",
    path: "dashboard/leads/:id/restore",
  },
  {
    event: "lead_status_changed",
    method: "PUT",
    path: "dashboard/leads/:id/status",
  },
  { event: "leads_imported", method: "POST", path: "dashboard/leads/import" },
  { event: "reminder_created", method: "POST", path: "dashboard/reminders" },
  {
    event: "reminder_deleted",
    method: "DELETE",
    path: "dashboard/reminders/:id",
  },
  { event: "reminder_updated", method: "PUT", path: "dashboard/reminders/:id" },
  {
    event: "reminder_calendar_event_added",
    method: "POST",
    path: "dashboard/reminders/:id/calendar-event",
  },
  {
    event: "reminder_online_meeting_created",
    method: "POST",
    path: "dashboard/reminders/:id/online-meeting",
  },
  {
    event: "reminder_restored",
    method: "POST",
    path: "dashboard/reminders/:id/restore",
  },
  // ─── Customers ───────────────────────────────────────────────────────────────
  {
    event: "customer_deleted",
    method: "DELETE",
    path: "dashboard/customers/:id/delete",
  },
  {
    event: "customer_restored",
    method: "POST",
    path: "dashboard/customers/:id/restore",
  },
  {
    event: "customer_updated",
    method: "PUT",
    path: "dashboard/customers/:id/update",
  },
  {
    event: "customer_created",
    method: "POST",
    path: "dashboard/customers/create",
    log: "customer creation completed",
    properties: ({ field }) => ({
      customer_type:
        String(field("is_company")) === "1" ? "company" : "individual",
    }),
  },
  {
    event: "customer_attachment_renamed",
    method: "PUT",
    path: "dashboard/customers/file/:id",
  },
  {
    event: "customer_quick_created",
    method: "POST",
    path: "dashboard/customers/quick-create",
  },
  {
    event: "customer_attachment_uploaded",
    method: "POST",
    path: "dashboard/customers/upload-files",
  },
  // ─── Deferral Schedules ──────────────────────────────────────────────────────
  {
    event: "deferral_schedule_created",
    method: "POST",
    path: "dashboard/deferral-schedules",
  },
  {
    event: "deferral_schedule_deleted",
    method: "DELETE",
    path: "dashboard/deferral-schedules/:id",
  },
  {
    event: "deferral_schedule_updated",
    method: "PUT",
    path: "dashboard/deferral-schedules/:id",
  },
  {
    event: "deferral_instalment_released",
    method: "POST",
    path: "dashboard/deferral-schedules/:id/instalments/:instalmentId/release",
  },
  // ─── Departments ─────────────────────────────────────────────────────────────
  {
    event: "department_deleted",
    method: "DELETE",
    path: "dashboard/departments/:id/delete",
  },
  {
    event: "department_restored",
    method: "POST",
    path: "dashboard/departments/:id/restore",
  },
  {
    event: "department_updated",
    method: "PUT",
    path: "dashboard/departments/:id/update",
  },
  {
    event: "department_created",
    method: "POST",
    path: "dashboard/departments/create",
  },
  // ─── Documents ───────────────────────────────────────────────────────────────
  {
    event: "document_category_created",
    method: "POST",
    path: "dashboard/document-categories",
  },
  {
    event: "document_category_deleted",
    method: "DELETE",
    path: "dashboard/document-categories/:id",
  },
  {
    event: "document_category_updated",
    method: "PUT",
    path: "dashboard/document-categories/:id",
  },
  {
    event: "document_category_restored",
    method: "POST",
    path: "dashboard/document-categories/:id/restore",
  },
  { event: "document_created", method: "POST", path: "dashboard/documents" },
  {
    event: "document_deleted",
    method: "DELETE",
    path: "dashboard/documents/:id",
  },
  { event: "document_updated", method: "PUT", path: "dashboard/documents/:id" },
  {
    event: "document_archived",
    method: "POST",
    path: "dashboard/documents/:id/archive",
  },
  {
    event: "document_version_deleted",
    method: "DELETE",
    path: "dashboard/documents/:id/files/:fileId",
  },
  {
    event: "document_version_made_current",
    method: "POST",
    path: "dashboard/documents/:id/files/:fileId/make-current",
  },
  {
    event: "document_renewed",
    method: "POST",
    path: "dashboard/documents/:id/renew",
  },
  {
    event: "document_restored",
    method: "POST",
    path: "dashboard/documents/:id/restore",
  },
  {
    event: "document_unarchived",
    method: "POST",
    path: "dashboard/documents/:id/unarchive",
  },
  // ─── Dues ────────────────────────────────────────────────────────────────────
  {
    event: "due_ejar_unlinked",
    method: "DELETE",
    path: "dashboard/dues/:id/unlink-ejar",
  },
  {
    event: "dues_collected",
    method: "POST",
    path: "dashboard/dues/collect-dues",
  },
  // ─── Ejar Contracts ──────────────────────────────────────────────────────────
  {
    event: "ejar_contract_imported",
    method: "POST",
    path: "dashboard/ejar/platform-contracts/:id/import",
    log: "ejar contract import completed",
    properties: ({ field }) => {
      const units = field("units");
      return { unit_count: Array.isArray(units) ? units.length : 0 };
    },
  },
  // ─── Employee Contracts ──────────────────────────────────────────────────────
  {
    event: "employee_contract_renewed",
    method: "POST",
    path: "dashboard/employee-contracts/:id/renew",
  },
  // ─── Employee Custody ────────────────────────────────────────────────────────
  {
    event: "employee_custody_approved",
    method: "POST",
    path: "dashboard/employee-custodies/:id/approve",
  },
  {
    event: "employee_custody_cancelled",
    method: "POST",
    path: "dashboard/employee-custodies/:id/cancel",
  },
  {
    event: "employee_custody_deleted",
    method: "DELETE",
    path: "dashboard/employee-custodies/:id/delete",
  },
  {
    event: "employee_custody_disbursed",
    method: "POST",
    path: "dashboard/employee-custodies/:id/disburse",
  },
  {
    event: "employee_custody_rejected",
    method: "POST",
    path: "dashboard/employee-custodies/:id/reject",
  },
  {
    event: "employee_custody_restored",
    method: "POST",
    path: "dashboard/employee-custodies/:id/restore",
  },
  {
    event: "employee_custody_settled",
    method: "POST",
    path: "dashboard/employee-custodies/:id/settle",
    props: ["type"],
  },
  {
    event: "employee_custody_submitted",
    method: "POST",
    path: "dashboard/employee-custodies/:id/submit",
  },
  {
    event: "employee_custody_updated",
    method: "PUT",
    path: "dashboard/employee-custodies/:id/update",
  },
  {
    event: "employee_custody_created",
    method: "POST",
    path: "dashboard/employee-custodies/create",
  },
  // ─── Employee Loans ──────────────────────────────────────────────────────────
  {
    event: "employee_loan_cancelled",
    method: "POST",
    path: "dashboard/employee-loans/:id/cancel",
  },
  {
    event: "employee_loan_deleted",
    method: "DELETE",
    path: "dashboard/employee-loans/:id/delete",
  },
  {
    event: "employee_loan_disbursed",
    method: "POST",
    path: "dashboard/employee-loans/:id/disburse",
  },
  {
    event: "employee_loan_restored",
    method: "POST",
    path: "dashboard/employee-loans/:id/restore",
  },
  {
    event: "employee_loan_settled",
    method: "POST",
    path: "dashboard/employee-loans/:id/settle",
  },
  {
    event: "employee_loan_updated",
    method: "PUT",
    path: "dashboard/employee-loans/:id/update",
  },
  {
    event: "employee_loan_waived",
    method: "POST",
    path: "dashboard/employee-loans/:id/waive",
  },
  {
    event: "employee_loan_created",
    method: "POST",
    path: "dashboard/employee-loans/create",
  },
  // ─── Employees ───────────────────────────────────────────────────────────────
  {
    event: "employee_position_deleted",
    method: "DELETE",
    path: "dashboard/employees-positions/:id/delete",
  },
  {
    event: "employee_position_restored",
    method: "POST",
    path: "dashboard/employees-positions/:id/restore",
  },
  {
    event: "employee_position_updated",
    method: "PUT",
    path: "dashboard/employees-positions/:id/update",
  },
  {
    event: "employee_position_created",
    method: "POST",
    path: "dashboard/employees-positions/create",
  },
  {
    event: "employee_deleted",
    method: "DELETE",
    path: "dashboard/employees/:id/delete",
  },
  {
    event: "employee_restored",
    method: "POST",
    path: "dashboard/employees/:id/restore",
  },
  {
    event: "employee_updated",
    method: "POST",
    path: "dashboard/employees/:id/update",
  },
  {
    event: "employee_created",
    method: "POST",
    path: "dashboard/employees/create",
  },
  {
    event: "salaries_paid",
    method: "POST",
    path: "dashboard/employees/salary-payout",
  },
  {
    event: "asset_assigned",
    method: "POST",
    path: "dashboard/inventories/assign-asset",
  },
  {
    event: "asset_unassigned",
    method: "POST",
    path: "dashboard/inventories/deassign-asset/:id",
  },
  // ─── End Of Service ──────────────────────────────────────────────────────────
  {
    event: "end_of_service_settlement_approved",
    method: "POST",
    path: "dashboard/end-of-services/:id/approve",
  },
  {
    event: "end_of_service_settlement_cancelled",
    method: "POST",
    path: "dashboard/end-of-services/:id/cancel",
  },
  {
    event: "end_of_service_settlement_deleted",
    method: "DELETE",
    path: "dashboard/end-of-services/:id/delete",
  },
  {
    event: "end_of_service_settlement_paid",
    method: "POST",
    path: "dashboard/end-of-services/:id/pay",
  },
  {
    event: "end_of_service_settlement_updated",
    method: "PUT",
    path: "dashboard/end-of-services/:id/update",
  },
  {
    event: "end_of_service_settlement_created",
    method: "POST",
    path: "dashboard/end-of-services/create",
  },
  // ─── Entities ────────────────────────────────────────────────────────────────
  {
    event: "entity_trademark_deleted",
    method: "DELETE",
    path: "dashboard/entities-trademarks/:id/delete",
  },
  {
    event: "entity_trademark_restored",
    method: "POST",
    path: "dashboard/entities-trademarks/:id/restore",
  },
  {
    event: "entity_trademark_updated",
    method: "PUT",
    path: "dashboard/entities-trademarks/:id/update",
  },
  {
    event: "entity_trademark_created",
    method: "POST",
    path: "dashboard/entities-trademarks/create",
  },
  {
    event: "entity_deleted",
    method: "DELETE",
    path: "dashboard/entities/:id/delete",
  },
  {
    event: "entity_restored",
    method: "POST",
    path: "dashboard/entities/:id/restore",
  },
  {
    event: "entity_updated",
    method: "PUT",
    path: "dashboard/entities/:id/update",
  },
  {
    event: "entity_created",
    method: "POST",
    path: "dashboard/entities/create",
  },
  {
    event: "entity_quick_created",
    method: "POST",
    path: "dashboard/entities/quick-create",
  },
  // ─── Esign ───────────────────────────────────────────────────────────────────
  {
    event: "esign_request_created",
    method: "POST",
    path: "dashboard/esign-requests",
  },
  {
    event: "esign_request_deleted",
    method: "DELETE",
    path: "dashboard/esign-requests/:id",
  },
  {
    event: "esign_request_updated",
    method: "PUT",
    path: "dashboard/esign-requests/:id",
  },
  {
    event: "esign_request_approved",
    method: "POST",
    path: "dashboard/esign-requests/:id/approve",
  },
  {
    event: "esign_request_cancelled",
    method: "POST",
    path: "dashboard/esign-requests/:id/cancel",
  },
  {
    event: "esign_request_rejected",
    method: "POST",
    path: "dashboard/esign-requests/:id/reject",
  },
  {
    event: "esign_invitation_resent",
    method: "POST",
    path: "dashboard/esign-requests/:id/resend",
  },
  {
    event: "esign_request_restored",
    method: "POST",
    path: "dashboard/esign-requests/:id/restore",
  },
  {
    event: "esign_request_review_submitted",
    method: "POST",
    path: "dashboard/esign-requests/:id/submit-review",
  },
  {
    event: "esign_request_synced",
    method: "POST",
    path: "dashboard/esign-requests/:id/sync",
  },
  {
    event: "esign_document_uploaded",
    method: "POST",
    path: "dashboard/esign-requests/upload-file",
  },
  // ─── Financial ───────────────────────────────────────────────────────────────
  {
    event: "journal_entry_updated",
    method: "PUT",
    path: "dashboard/financial-entries/:id/update",
  },
  {
    event: "journal_entry_created",
    method: "POST",
    path: "dashboard/financial-entries/create",
  },
  // ─── Financial Trees ─────────────────────────────────────────────────────────
  {
    event: "financial_account_created",
    method: "POST",
    path: "dashboard/financial-trees",
  },
  {
    event: "financial_account_deleted",
    method: "DELETE",
    path: "dashboard/financial-trees/:id",
  },
  {
    event: "financial_account_updated",
    method: "PUT",
    path: "dashboard/financial-trees/:id",
  },
  {
    event: "financial_account_restored",
    method: "POST",
    path: "dashboard/financial-trees/:id/restore",
  },
  // ─── Fixed Assets ────────────────────────────────────────────────────────────
  {
    event: "fixed_asset_created",
    method: "POST",
    path: "dashboard/fixed-assets",
  },
  {
    event: "fixed_asset_deleted",
    method: "DELETE",
    path: "dashboard/fixed-assets/:id",
  },
  {
    event: "fixed_asset_updated",
    method: "PUT",
    path: "dashboard/fixed-assets/:id",
  },
  {
    event: "fixed_asset_disposed",
    method: "POST",
    path: "dashboard/fixed-assets/:id/dispose",
  },
  {
    event: "fixed_asset_restored",
    method: "POST",
    path: "dashboard/fixed-assets/:id/restore",
  },
  {
    event: "depreciation_run",
    method: "POST",
    path: "dashboard/fixed-assets/depreciation-runs",
  },
  // ─── Home ────────────────────────────────────────────────────────────────────
  {
    event: "home_widget_visibility_updated",
    method: "PUT",
    path: "dashboard/statistics/home/preferences",
  },
  // ─── Inventory ───────────────────────────────────────────────────────────────
  {
    event: "inventory_product_deleted",
    method: "DELETE",
    path: "dashboard/inventories/:id/delete",
  },
  {
    event: "inventory_product_restored",
    method: "POST",
    path: "dashboard/inventories/:id/restore",
  },
  {
    event: "inventory_product_updated",
    method: "POST",
    path: "dashboard/inventories/:id/update",
  },
  {
    event: "inventory_product_created",
    method: "POST",
    path: "dashboard/inventories/create",
  },
  {
    event: "inventory_category_deleted",
    method: "DELETE",
    path: "dashboard/inventory-categories/:id/delete",
  },
  {
    event: "inventory_category_restored",
    method: "POST",
    path: "dashboard/inventory-categories/:id/restore",
  },
  {
    event: "inventory_category_updated",
    method: "PUT",
    path: "dashboard/inventory-categories/:id/update",
  },
  {
    event: "inventory_category_created",
    method: "POST",
    path: "dashboard/inventory-categories/create",
  },
  {
    event: "inventory_quantity_type_deleted",
    method: "DELETE",
    path: "dashboard/inventory-quantity-types/:id/delete",
  },
  {
    event: "inventory_quantity_type_restored",
    method: "POST",
    path: "dashboard/inventory-quantity-types/:id/restore",
  },
  {
    event: "inventory_quantity_type_updated",
    method: "PUT",
    path: "dashboard/inventory-quantity-types/:id/update",
  },
  {
    event: "inventory_quantity_type_created",
    method: "POST",
    path: "dashboard/inventory-quantity-types/create",
  },
  {
    event: "inventory_destruction_voucher_created",
    method: "POST",
    path: "dashboard/inventory-vouchers/create-destruction",
  },
  {
    event: "inventory_return_voucher_created",
    method: "POST",
    path: "dashboard/inventory-vouchers/create-return",
  },
  {
    event: "inventory_supply_voucher_created",
    method: "POST",
    path: "dashboard/inventory-vouchers/create-supply",
  },
  {
    event: "inventory_transfer_voucher_created",
    method: "POST",
    path: "dashboard/inventory-vouchers/create-transfer",
  },
  {
    event: "inventory_withdraw_voucher_created",
    method: "POST",
    path: "dashboard/inventory-vouchers/create-withdraw",
  },
  // ─── Invoices ────────────────────────────────────────────────────────────────
  {
    event: "invoice_collected",
    method: "POST",
    path: "dashboard/invoices/collect/:id",
    props: ["type"],
  },
  {
    event: "credit_note_created",
    method: "POST",
    path: "dashboard/invoices/create-credit-note",
    props: ["type"],
  },
  {
    event: "debit_note_created",
    method: "POST",
    path: "dashboard/invoices/create-debit-note",
    props: ["type"],
  },
  {
    event: "purchase_order_created",
    method: "POST",
    path: "dashboard/invoices/create-po-invoice",
  },
  {
    event: "purchase_invoice_created",
    method: "POST",
    path: "dashboard/invoices/create-purchase-invoice",
  },
  {
    event: "purchase_request_created",
    method: "POST",
    path: "dashboard/invoices/create-rpo-invoice",
  },
  {
    event: "sales_invoice_created",
    method: "POST",
    path: "dashboard/invoices/create-sales-invoice",
  },
  {
    event: "purchase_invoice_paid_from_balance",
    method: "GET",
    path: "dashboard/invoices/purchase/:id/pay-with-entity-balance",
  },
  {
    event: "sales_invoice_paid_from_balance",
    method: "GET",
    path: "dashboard/invoices/sales/:id/pay-with-customer-balance",
  },
  // ─── Leaves ──────────────────────────────────────────────────────────────────
  {
    event: "leave_type_deleted",
    method: "DELETE",
    path: "dashboard/leave-types/:id/delete",
  },
  {
    event: "leave_type_restored",
    method: "POST",
    path: "dashboard/leave-types/:id/restore",
  },
  {
    event: "leave_type_updated",
    method: "PUT",
    path: "dashboard/leave-types/:id/update",
  },
  {
    event: "leave_type_created",
    method: "POST",
    path: "dashboard/leave-types/create",
  },
  {
    event: "leave_cancelled",
    method: "POST",
    path: "dashboard/leaves/:id/cancel",
  },
  {
    event: "leave_decided",
    method: "POST",
    path: "dashboard/leaves/:id/decide",
    props: ["status"],
  },
  {
    event: "leave_deleted",
    method: "DELETE",
    path: "dashboard/leaves/:id/delete",
  },
  {
    event: "leave_restored",
    method: "POST",
    path: "dashboard/leaves/:id/restore",
  },
  { event: "leave_created", method: "POST", path: "dashboard/leaves/create" },
  // ─── Letters ─────────────────────────────────────────────────────────────────
  {
    event: "letter_template_created",
    method: "POST",
    path: "dashboard/letter-templates",
  },
  {
    event: "letter_template_deleted",
    method: "DELETE",
    path: "dashboard/letter-templates/:id",
  },
  {
    event: "letter_template_updated",
    method: "PUT",
    path: "dashboard/letter-templates/:id",
  },
  {
    event: "letter_template_restored",
    method: "POST",
    path: "dashboard/letter-templates/:id/restore",
  },
  {
    event: "letter_type_created",
    method: "POST",
    path: "dashboard/letter-types",
  },
  {
    event: "letter_type_deleted",
    method: "DELETE",
    path: "dashboard/letter-types/:id",
  },
  {
    event: "letter_type_updated",
    method: "PUT",
    path: "dashboard/letter-types/:id",
  },
  {
    event: "letter_type_restored",
    method: "POST",
    path: "dashboard/letter-types/:id/restore",
  },
  { event: "letter_created", method: "POST", path: "dashboard/letters" },
  { event: "letter_deleted", method: "DELETE", path: "dashboard/letters/:id" },
  { event: "letter_updated", method: "PUT", path: "dashboard/letters/:id" },
  {
    event: "letter_approved",
    method: "POST",
    path: "dashboard/letters/:id/approve",
  },
  {
    event: "letter_issued",
    method: "POST",
    path: "dashboard/letters/:id/issue",
  },
  {
    event: "letter_rejected",
    method: "POST",
    path: "dashboard/letters/:id/reject",
  },
  {
    event: "letter_restored",
    method: "POST",
    path: "dashboard/letters/:id/restore",
  },
  {
    event: "letter_reviewed",
    method: "POST",
    path: "dashboard/letters/:id/review",
  },
  { event: "letter_sent", method: "POST", path: "dashboard/letters/:id/send" },
  {
    event: "letter_approval_submitted",
    method: "POST",
    path: "dashboard/letters/:id/submit-approval",
  },
  {
    event: "letter_review_submitted",
    method: "POST",
    path: "dashboard/letters/:id/submit-review",
  },
  {
    event: "letter_attachment_renamed",
    method: "POST",
    path: "dashboard/letters/file/:id",
  },
  {
    event: "letter_attachment_uploaded",
    method: "POST",
    path: "dashboard/letters/upload-file",
  },
  // ─── Maintenance ─────────────────────────────────────────────────────────────
  {
    event: "maintenance_category_created",
    method: "POST",
    path: "dashboard/maintenance-categories",
  },
  {
    event: "maintenance_category_deleted",
    method: "DELETE",
    path: "dashboard/maintenance-categories/:id",
  },
  {
    event: "maintenance_category_updated",
    method: "PUT",
    path: "dashboard/maintenance-categories/:id",
  },
  {
    event: "maintenance_category_restored",
    method: "POST",
    path: "dashboard/maintenance-categories/:id/restore",
  },
  {
    event: "maintenance_request_created",
    method: "POST",
    path: "dashboard/maintenance-requests",
  },
  {
    event: "maintenance_request_deleted",
    method: "DELETE",
    path: "dashboard/maintenance-requests/:id",
  },
  {
    event: "maintenance_request_updated",
    method: "PUT",
    path: "dashboard/maintenance-requests/:id",
  },
  {
    event: "maintenance_request_restored",
    method: "POST",
    path: "dashboard/maintenance-requests/:id/restore",
  },
  {
    event: "preset_read_created",
    method: "POST",
    path: "dashboard/preset-reads",
  },
  {
    event: "preset_read_deleted",
    method: "DELETE",
    path: "dashboard/preset-reads/:id",
  },
  {
    event: "preset_read_updated",
    method: "PUT",
    path: "dashboard/preset-reads/:id",
  },
  {
    event: "preset_read_restored",
    method: "POST",
    path: "dashboard/preset-reads/:id/restore",
  },
  {
    event: "preset_remark_created",
    method: "POST",
    path: "dashboard/preset-remarks",
  },
  {
    event: "preset_remark_deleted",
    method: "DELETE",
    path: "dashboard/preset-remarks/:id",
  },
  {
    event: "preset_remark_updated",
    method: "PUT",
    path: "dashboard/preset-remarks/:id",
  },
  {
    event: "preset_remark_restored",
    method: "POST",
    path: "dashboard/preset-remarks/:id/restore",
  },
  {
    event: "spare_parts_request_status_changed",
    method: "PUT",
    path: "dashboard/spare-parts-requests/:id",
    props: ["status"],
  },
  {
    event: "spare_parts_request_restored",
    method: "POST",
    path: "dashboard/spare-parts-requests/:id/restore",
  },
  {
    event: "work_order_created",
    method: "POST",
    path: "dashboard/work-orders",
  },
  {
    event: "work_order_restored",
    method: "POST",
    path: "dashboard/work-orders/:id/restore",
  },
  // ─── Notes ───────────────────────────────────────────────────────────────────
  { event: "note_created", method: "POST", path: "dashboard/notes" },
  { event: "note_deleted", method: "DELETE", path: "dashboard/notes/:id" },
  { event: "note_updated", method: "PUT", path: "dashboard/notes/:id" },
  {
    event: "note_restored",
    method: "POST",
    path: "dashboard/notes/:id/restore",
  },
  // ─── Notifications ───────────────────────────────────────────────────────────
  {
    event: "browser_push_token_unregistered",
    method: "POST",
    path: "dashboard/delete-fcm-token",
  },
  {
    event: "notification_template_channel_saved",
    method: "PUT",
    path: "dashboard/notification-templates",
  },
  {
    event: "notification_template_default_reset",
    method: "POST",
    path: "dashboard/notification-templates/reset",
  },
  {
    event: "notification_template_channel_toggled",
    method: "POST",
    path: "dashboard/notification-templates/toggle",
  },
  {
    event: "browser_push_token_registered",
    method: "POST",
    path: "dashboard/save-fcm-token",
  },
  // ─── Opening Balances ────────────────────────────────────────────────────────
  {
    event: "opening_balance_import_created",
    method: "POST",
    path: "dashboard/opening-balances",
  },
  {
    event: "opening_balance_import_updated",
    method: "PUT",
    path: "dashboard/opening-balances/:id",
  },
  {
    event: "opening_balance_posted",
    method: "POST",
    path: "dashboard/opening-balances/:id/post",
  },
  {
    event: "opening_balance_reversed",
    method: "POST",
    path: "dashboard/opening-balances/:id/reverse",
  },
  {
    event: "opening_balance_validated",
    method: "POST",
    path: "dashboard/opening-balances/:id/validate",
  },
  // ─── Payroll ─────────────────────────────────────────────────────────────────
  {
    event: "payroll_run_approved",
    method: "POST",
    path: "dashboard/payroll-runs/:id/approve",
  },
  {
    event: "payroll_run_cancelled",
    method: "POST",
    path: "dashboard/payroll-runs/:id/cancel",
  },
  {
    event: "payroll_run_deleted",
    method: "DELETE",
    path: "dashboard/payroll-runs/:id/delete",
  },
  {
    event: "payroll_run_paid",
    method: "POST",
    path: "dashboard/payroll-runs/:id/pay",
  },
  {
    event: "payroll_run_recalculated",
    method: "POST",
    path: "dashboard/payroll-runs/:id/recalculate",
  },
  {
    event: "payroll_run_rejected",
    method: "POST",
    path: "dashboard/payroll-runs/:id/reject",
  },
  {
    event: "payroll_run_restored",
    method: "POST",
    path: "dashboard/payroll-runs/:id/restore",
  },
  {
    event: "payroll_run_submitted",
    method: "POST",
    path: "dashboard/payroll-runs/:id/submit",
  },
  {
    event: "payroll_run_updated",
    method: "PUT",
    path: "dashboard/payroll-runs/:id/update",
  },
  {
    event: "payroll_run_created",
    method: "POST",
    path: "dashboard/payroll-runs/create",
  },
  // ─── Pos ─────────────────────────────────────────────────────────────────────
  { event: "pos_created", method: "POST", path: "dashboard/pos" },
  { event: "pos_deleted", method: "DELETE", path: "dashboard/pos/:id" },
  { event: "pos_updated", method: "PUT", path: "dashboard/pos/:id" },
  { event: "pos_restored", method: "POST", path: "dashboard/pos/:id/restore" },
  // ─── Price Quotations ────────────────────────────────────────────────────────
  {
    event: "price_quotation_created",
    method: "POST",
    path: "dashboard/price-quotations",
  },
  {
    event: "price_quotation_deleted",
    method: "DELETE",
    path: "dashboard/price-quotations/:id",
  },
  {
    event: "price_quotation_updated",
    method: "PUT",
    path: "dashboard/price-quotations/:id",
  },
  {
    event: "price_quotation_status_changed",
    method: "POST",
    path: "dashboard/price-quotations/:id/change-status",
    props: ["status"],
  },
  {
    event: "price_quotation_comment_added",
    method: "POST",
    path: "dashboard/price-quotations/:id/comments",
  },
  {
    event: "price_quotation_restored",
    method: "POST",
    path: "dashboard/price-quotations/:id/restore",
  },
  {
    event: "price_quotation_sent",
    method: "POST",
    path: "dashboard/price-quotations/:id/send",
  },
  // ─── Projects ────────────────────────────────────────────────────────────────
  { event: "attachment_renamed", path: "dashboard/attachments/:id/update" },
  { event: "project_created", method: "POST", path: "dashboard/projects" },
  {
    event: "project_deleted",
    method: "DELETE",
    path: "dashboard/projects/:id",
  },
  { event: "project_updated", method: "PUT", path: "dashboard/projects/:id" },
  {
    event: "project_restored",
    method: "POST",
    path: "dashboard/projects/:id/restore",
  },
  {
    event: "project_attachment_uploaded",
    method: "POST",
    path: "dashboard/projects/upload-file",
  },
  {
    event: "project_image_uploaded",
    method: "POST",
    path: "dashboard/projects/upload-image",
  },
  // ─── Promotions ──────────────────────────────────────────────────────────────
  {
    event: "unit_promotion_purchased",
    method: "POST",
    path: "dashboard/units-promotions/add",
  },
  // ─── Properties ──────────────────────────────────────────────────────────────
  {
    event: "property_deleted",
    method: "DELETE",
    path: "dashboard/properties/:id/delete",
  },
  {
    event: "property_restored",
    method: "POST",
    path: "dashboard/properties/:id/restore",
  },
  {
    event: "property_created",
    method: "POST",
    path: "dashboard/properties/create-with-unit",
  },
  {
    event: "property_updated",
    method: "PUT",
    path: "dashboard/properties/update-with-unit/:id",
  },
  // ─── Provisions ──────────────────────────────────────────────────────────────
  {
    event: "doubtful_debt_provision_posted",
    method: "POST",
    path: "dashboard/doubtful-debt-provisions/post",
  },
  {
    event: "receivable_write_off_created",
    method: "POST",
    path: "dashboard/receivable-writeoffs/create",
  },
  // ─── Recurring Journals ──────────────────────────────────────────────────────
  {
    event: "recurring_journal_created",
    method: "POST",
    path: "dashboard/recurring-journals",
  },
  {
    event: "recurring_journal_deleted",
    method: "DELETE",
    path: "dashboard/recurring-journals/:id",
  },
  {
    event: "recurring_journal_updated",
    method: "PUT",
    path: "dashboard/recurring-journals/:id",
  },
  {
    event: "recurring_journal_restored",
    method: "POST",
    path: "dashboard/recurring-journals/:id/restore",
  },
  {
    event: "recurring_journal_run",
    method: "POST",
    path: "dashboard/recurring-journals/:id/run",
  },
  // ─── Roles ───────────────────────────────────────────────────────────────────
  { event: "role_created", method: "POST", path: "dashboard/roles/create" },
  {
    event: "role_deleted",
    method: "DELETE",
    path: "dashboard/roles/delete/:id",
  },
  { event: "role_updated", method: "PUT", path: "dashboard/roles/update/:id" },
  // ─── Sanadat ─────────────────────────────────────────────────────────────────
  {
    event: "payment_voucher_created",
    method: "POST",
    path: "dashboard/vouchers",
    when: ({ field }) => field("type") === "payment",
    log: "payment voucher creation completed",
  },
  {
    event: "receipt_voucher_created",
    method: "POST",
    path: "dashboard/vouchers",
    when: ({ field }) => field("type") === "receipt",
  },
  // ─── Service Contracts ───────────────────────────────────────────────────────
  {
    event: "service_contract_created",
    method: "POST",
    path: "dashboard/service-contracts",
  },
  {
    event: "service_contract_updated",
    method: "PUT",
    path: "dashboard/service-contracts/:id",
  },
  {
    event: "service_contract_published",
    method: "POST",
    path: "dashboard/service-contracts/:id/publish",
  },
  // ─── Services ────────────────────────────────────────────────────────────────
  {
    event: "service_deleted",
    method: "DELETE",
    path: "dashboard/services/:id/delete",
  },
  {
    event: "service_restored",
    method: "POST",
    path: "dashboard/services/:id/restore",
  },
  {
    event: "service_updated",
    method: "PUT",
    path: "dashboard/services/:id/update",
  },
  {
    event: "service_created",
    method: "POST",
    path: "dashboard/services/create",
  },
  // ─── Settings ────────────────────────────────────────────────────────────────
  {
    event: "ejar_integration_activated",
    method: "POST",
    path: "dashboard/agent/integrations/ejar/activate",
  },
  {
    event: "nafath_integration_activated",
    method: "POST",
    path: "dashboard/agent/integrations/nafath/activate",
  },
  {
    event: "payment_gateway_integration_activated",
    method: "POST",
    path: "dashboard/agent/integrations/payment_gateway/activate",
  },
  {
    event: "zatca_integration_activated",
    method: "POST",
    path: "dashboard/agent/integrations/zatca/activate",
  },
  {
    event: "ads_cross_listing_settings_updated",
    method: "PUT",
    path: "dashboard/agent/settings/ads-cross-listing",
  },
  {
    event: "marketplace_settings_updated",
    method: "PUT",
    path: "dashboard/agent/settings/agent_owned_marketplace",
  },
  {
    event: "billing_info_updated",
    method: "PUT",
    path: "dashboard/agent/settings/billing-info",
  },
  {
    event: "communication_channels_updated",
    method: "PUT",
    path: "dashboard/agent/settings/communication-channels",
  },
  {
    event: "company_info_updated",
    method: "PUT",
    path: "dashboard/agent/settings/company_info",
  },
  {
    event: "contract_serial_prefixes_updated",
    method: "PUT",
    path: "dashboard/agent/settings/contract_serial_prefixes",
  },
  {
    event: "crm_settings_updated",
    method: "PUT",
    path: "dashboard/agent/settings/crm",
  },
  {
    event: "web_to_lead_key_rotated",
    method: "POST",
    path: "dashboard/agent/settings/crm/web-to-lead-key",
  },
  {
    event: "document_prefixes_updated",
    method: "PUT",
    path: "dashboard/agent/settings/documents_prefixes",
  },
  {
    event: "ejar_credentials_updated",
    method: "PUT",
    path: "dashboard/agent/settings/ejar-credentials",
  },
  {
    event: "hr_settings_updated",
    method: "PUT",
    path: "dashboard/agent/settings/hr",
  },
  {
    event: "letter_workflow_settings_updated",
    method: "PUT",
    path: "dashboard/agent/settings/letters",
  },
  {
    event: "sadq_settings_updated",
    method: "PUT",
    path: "dashboard/agent/settings/sadq",
  },
  {
    event: "sadq_webhook_registered",
    method: "POST",
    path: "dashboard/agent/settings/sadq/webhook",
  },
  {
    event: "zatca_integrated",
    method: "POST",
    path: "dashboard/agent/zatca/integrate",
  },
  {
    event: "zatca_integration_revoked",
    method: "POST",
    path: "dashboard/agent/zatca/revoke",
  },
  {
    event: "data_migration_file_uploaded",
    method: "POST",
    path: "dashboard/data-migration",
  },
  {
    event: "data_migration_batch_deleted",
    method: "DELETE",
    path: "dashboard/data-migration/:id",
  },
  {
    event: "data_migration_import_started",
    method: "POST",
    path: "dashboard/data-migration/:id/import",
  },
  {
    event: "fiscal_year_start_month_updated",
    method: "PUT",
    path: "dashboard/fiscal-year/settings",
  },
  {
    event: "landing_page_section_updated",
    method: "PATCH",
    path: "dashboard/sections/:id",
  },
  {
    event: "landing_page_sections_reordered",
    method: "POST",
    path: "dashboard/sections/reorder",
  },
  {
    event: "moyasar_merchant_created",
    method: "POST",
    path: "moyasar/merchant",
  },
  {
    event: "moyasar_merchant_documents_uploaded",
    method: "POST",
    path: "moyasar/merchant/documents",
  },
  {
    event: "moyasar_merchant_document_deleted",
    method: "DELETE",
    path: "moyasar/merchant/documents/:id",
  },
  {
    event: "moyasar_merchant_review_submitted",
    method: "POST",
    path: "moyasar/merchant/submit-review",
  },
  // ─── Shared ──────────────────────────────────────────────────────────────────
  {
    event: "attachment_renamed",
    method: "POST",
    path: "dashboard/employees/file/:id",
  },
  // ─── Subscriptions ───────────────────────────────────────────────────────────
  {
    event: "subscription_payment_card_added",
    method: "POST",
    path: "account/cards",
  },
  {
    event: "subscription_invoice_paid",
    method: "POST",
    path: "account/invoices/:id/pay",
  },
  {
    event: "subscription_seats_purchased",
    method: "POST",
    path: "account/subscription/purchase-seats",
  },
  {
    event: "subscription_plan_changed",
    method: "POST",
    path: "account/subscription/upgrade",
  },
  // ─── Support ─────────────────────────────────────────────────────────────────
  {
    event: "contact_message_deleted",
    method: "DELETE",
    path: "dashboard/contact-messages/:id",
  },
  {
    event: "contact_message_restored",
    method: "POST",
    path: "dashboard/contact-messages/:id/restore",
  },
  {
    event: "contact_message_status_changed",
    method: "PATCH",
    path: "dashboard/contact-messages/:id/status",
  },
  {
    event: "support_ticket_status_changed",
    method: "PUT",
    path: "dashboard/support-center-tickets/change-status/:id",
  },
  {
    event: "support_ticket_deleted",
    method: "DELETE",
    path: "dashboard/support-center-tickets/delete/:id",
  },
  {
    event: "support_ticket_restored",
    method: "POST",
    path: "dashboard/support-center-tickets/restore/:id",
  },
  {
    event: "support_ticket_replied",
    method: "POST",
    path: "dashboard/support-center-tickets/send-reply/:id",
  },
  // ─── Tasks ───────────────────────────────────────────────────────────────────
  {
    event: "task_status_changed",
    method: "POST",
    path: "dashboard/task/change-status/:id",
    props: ["status"],
  },
  {
    event: "task_comment_replied",
    method: "POST",
    path: "dashboard/task/reply/:id",
  },
  {
    event: "task_completed",
    method: "POST",
    path: "dashboard/task/terminate/:id",
  },
  { event: "task_created", method: "POST", path: "dashboard/tasks" },
  { event: "task_deleted", method: "DELETE", path: "dashboard/tasks/:id" },
  { event: "task_updated", method: "PUT", path: "dashboard/tasks/:id" },
  {
    event: "task_restored",
    method: "POST",
    path: "dashboard/tasks/:id/restore",
  },
  // ─── Taxes ───────────────────────────────────────────────────────────────────
  { event: "tax_created", method: "POST", path: "dashboard/taxes" },
  { event: "tax_updated", method: "PUT", path: "dashboard/taxes/:id" },
  {
    event: "tax_deleted",
    method: "DELETE",
    path: "dashboard/taxes/:id/delete",
  },
  {
    event: "tax_restored",
    method: "POST",
    path: "dashboard/taxes/:id/restore",
  },
  // ─── Topbar ──────────────────────────────────────────────────────────────────
  {
    event: "notification_marked_read",
    method: "PATCH",
    path: "dashboard/notifications/:id/read",
  },
  {
    event: "notification_marked_unread",
    method: "PATCH",
    path: "dashboard/notifications/:id/unread",
  },
  {
    event: "notifications_marked_all_read",
    method: "POST",
    path: "dashboard/notifications/read-all",
  },
  // ─── Units ───────────────────────────────────────────────────────────────────
  {
    event: "equipment_unit_assigned",
    method: "POST",
    path: "dashboard/inventories/assign-equipment",
  },
  {
    event: "equipment_unit_unassigned",
    method: "POST",
    path: "dashboard/inventories/deassign-equipment/:id",
  },
  {
    event: "sell_contract_cancelled",
    method: "PUT",
    path: "dashboard/sell-contracts/:id/cancel",
  },
  {
    event: "service_unit_assigned",
    method: "POST",
    path: "dashboard/service-assigns/create",
  },
  {
    event: "assigned_service_stopped",
    method: "PUT",
    path: "dashboard/services/finish-assign/:id",
  },
  {
    event: "assigned_service_resumed",
    method: "PUT",
    path: "dashboard/services/reassign/:id",
  },
  {
    event: "unit_conversation_marked_read",
    method: "POST",
    path: "dashboard/unit-messages/read/:id",
  },
  {
    event: "unit_message_sent",
    method: "POST",
    path: "dashboard/unit-messages/send",
  },
  {
    event: "unit_added_to_marketplace",
    method: "POST",
    path: "dashboard/units/:id/add-to-marketplace",
  },
  {
    event: "unit_expense_allocated",
    method: "POST",
    path: "dashboard/units/:id/allocate-expense",
  },
  {
    event: "blackout_period_created",
    method: "POST",
    path: "dashboard/units/:id/blackout-periods",
  },
  {
    event: "blackout_period_deleted",
    method: "DELETE",
    path: "dashboard/units/:id/blackout-periods/:id",
  },
  {
    event: "blackout_period_updated",
    method: "PUT",
    path: "dashboard/units/:id/blackout-periods/:id",
  },
  {
    event: "unit_cloned",
    method: "POST",
    path: "dashboard/units/:id/clone",
    properties: ({ response, meta }) => ({
      unit_type: meta?.unit_type ?? response?.data?.type,
    }),
  },
  {
    event: "unit_deleted",
    method: "DELETE",
    path: "dashboard/units/:id/delete",
  },
  {
    event: "distribution_cycle_created",
    method: "POST",
    path: "dashboard/units/:id/distribution-cycles",
  },
  {
    event: "distribution_cycle_cancelled",
    method: "POST",
    path: "dashboard/units/:id/distribution-cycles/:id/cancel",
  },
  {
    event: "distribution_cycle_earnings_released",
    method: "POST",
    path: "dashboard/units/:id/distribution-cycles/:id/release-earnings",
  },
  {
    event: "unit_party_deposit_recorded",
    method: "POST",
    path: "dashboard/units/:id/owners/:id/deposit",
  },
  {
    event: "unit_party_balance_released",
    method: "POST",
    path: "dashboard/units/:id/owners/:id/release-balance",
  },
  {
    event: "unit_party_created",
    method: "POST",
    path: "dashboard/units/:id/parties",
  },
  {
    event: "unit_party_deleted",
    method: "DELETE",
    path: "dashboard/units/:id/parties/:id",
  },
  {
    event: "unit_party_updated",
    method: "PUT",
    path: "dashboard/units/:id/parties/:id",
  },
  {
    event: "pricing_model_created",
    method: "POST",
    path: "dashboard/units/:id/pricing-models",
  },
  {
    event: "pricing_model_deleted",
    method: "DELETE",
    path: "dashboard/units/:id/pricing-models/:id",
  },
  {
    event: "pricing_model_updated",
    method: "PUT",
    path: "dashboard/units/:id/pricing-models/:id",
  },
  {
    event: "pricing_model_set_default",
    method: "POST",
    path: "dashboard/units/:id/pricing-models/:id/default",
  },
  {
    event: "unit_restored",
    method: "POST",
    path: "dashboard/units/:id/restore",
  },
  {
    event: "special_price_period_created",
    method: "POST",
    path: "dashboard/units/:id/special-price-periods",
  },
  {
    event: "special_price_period_deleted",
    method: "DELETE",
    path: "dashboard/units/:id/special-price-periods/:id",
  },
  {
    event: "special_price_period_updated",
    method: "PUT",
    path: "dashboard/units/:id/special-price-periods/:id",
  },
  {
    event: "unit_marketplace_advertising_stopped",
    method: "POST",
    path: "dashboard/units/:id/stop-marketplace-advertising",
  },
  { event: "unit_updated", method: "PUT", path: "dashboard/units/:id/update" },
  { event: "unit_created", method: "POST", path: "dashboard/units/create" },
  {
    event: "unit_document_uploaded",
    method: "POST",
    path: "dashboard/units/upload-document",
  },
  {
    event: "unit_attachment_uploaded",
    method: "POST",
    path: "dashboard/units/upload-file",
  },
  {
    event: "unit_image_uploaded",
    method: "POST",
    path: "dashboard/units/upload-image",
  },
  // ─── Uploads ─────────────────────────────────────────────────────────────────
  {
    event: "employee_attachment_uploaded",
    method: "POST",
    path: "dashboard/employees/upload-file",
  },
  // ─── Wallet ──────────────────────────────────────────────────────────────────
  {
    event: "ai_auto_consume_toggled",
    method: "PUT",
    path: "dashboard/wallet/ai-auto-consume",
  },
  {
    event: "wallet_card_added",
    method: "POST",
    path: "dashboard/wallet/cards",
  },
  {
    event: "wallet_card_deleted",
    method: "DELETE",
    path: "dashboard/wallet/cards/:id",
  },
  {
    event: "wallet_card_set_default",
    method: "POST",
    path: "dashboard/wallet/cards/:id/default",
  },
  {
    event: "wallet_charged_new_card",
    method: "POST",
    path: "dashboard/wallet/charge/new-card",
  },
  {
    event: "wallet_charged_saved_card",
    method: "POST",
    path: "dashboard/wallet/charge/saved-card",
  },
  {
    event: "wallet_settings_updated",
    method: "PUT",
    path: "dashboard/wallet/settings",
  },
  // ─── Warehouses ──────────────────────────────────────────────────────────────
  {
    event: "assets_warehouse_deleted",
    method: "DELETE",
    path: "dashboard/warehouses/assets/:id/delete",
  },
  {
    event: "assets_warehouse_restored",
    method: "POST",
    path: "dashboard/warehouses/assets/:id/restore",
  },
  {
    event: "assets_warehouse_updated",
    method: "PUT",
    path: "dashboard/warehouses/assets/:id/update",
  },
  {
    event: "assets_warehouse_created",
    method: "POST",
    path: "dashboard/warehouses/assets/create",
  },
  {
    event: "equipments_warehouse_deleted",
    method: "DELETE",
    path: "dashboard/warehouses/equipments/:id/delete",
  },
  {
    event: "equipments_warehouse_restored",
    method: "POST",
    path: "dashboard/warehouses/equipments/:id/restore",
  },
  {
    event: "equipments_warehouse_updated",
    method: "PUT",
    path: "dashboard/warehouses/equipments/:id/update",
  },
  {
    event: "equipments_warehouse_created",
    method: "POST",
    path: "dashboard/warehouses/equipments/create",
  },
  {
    event: "pos_warehouse_deleted",
    method: "DELETE",
    path: "dashboard/warehouses/pos/:id/delete",
  },
  {
    event: "pos_warehouse_restored",
    method: "POST",
    path: "dashboard/warehouses/pos/:id/restore",
  },
  {
    event: "pos_warehouse_updated",
    method: "PUT",
    path: "dashboard/warehouses/pos/:id/update",
  },
  {
    event: "pos_warehouse_created",
    method: "POST",
    path: "dashboard/warehouses/pos/create",
  },
  {
    event: "spareparts_warehouse_deleted",
    method: "DELETE",
    path: "dashboard/warehouses/spareparts/:id/delete",
  },
  {
    event: "spareparts_warehouse_restored",
    method: "POST",
    path: "dashboard/warehouses/spareparts/:id/restore",
  },
  {
    event: "spareparts_warehouse_updated",
    method: "PUT",
    path: "dashboard/warehouses/spareparts/:id/update",
  },
  {
    event: "spareparts_warehouse_created",
    method: "POST",
    path: "dashboard/warehouses/spareparts/create",
  },
];
